



































































































































































import {
	computed,
	defineComponent,
	PropType,
	ref,
	Ref,
	watch,
} from '@vue/composition-api';
import deepequal from 'deep-equal';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { Size, Spacing } from '@front-app/components/constants';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import { EventBusEvent, useEventBus } from '@front-app/services/EventBus';
import { STATUS } from '@front-app/constants/state';
import { getGroupContentStore } from '@front-app/store/modules/groupContent';
import { GroupsStore } from '@front-app/store/singletons/Groups';
import { useConfirmDialog } from '@vueuse/core';
import {
	ContentSort,
	ContentOrdering,
	ContentPageParamsV2,
	getGroupContentV2,
} from '@front-app/services/GroupsApi/GroupsContentApi';
import {
	Content,
	ContentType,
	ContentV2,
	ReactionType,
} from '@front-app/services/GroupsApi/DiscussionModel';
import { useCheckboxes } from '@front-app/hooks/useCheckboxes';
import { useRadioButtons } from '@front-app/hooks/useRadioButtons';
import { toWildcardSearch } from '@front-app/util/contentSearch';
import { Routes } from '@front-app/constants/routes';
import { deleteFile } from '@front-app/services/GroupsApi/GroupFileApi';
import { deleteLink } from '@front-app/services/GroupsApi/GroupLinkApi';
import PaddedContent from '@front-app/components/layouts/columns/PaddedContent.vue';
import LoaderState from '@front-app/components/LoaderState.vue';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import ScrollingColumn from '@front-app/components/layouts/columns/ScrollingColumn.vue';
import SearchBox from '@front-app/components/search/SearchBox.vue';
import CheckboxList from '@front-app/components/inputs/CheckboxList.vue';
import RadioList from '@front-app/components/inputs/RadioList.vue';
import SlidingPanel from '@front-app/components/modals/SlidingPanel.vue';
import ContentInfoModal from '@front-app/components/cards/content/ContentInfoModal.vue';
import CategoriesList from '@front-app/components/group/CategoriesList.vue';
import MixedContentCard from '@front-app/components/cards/content/MixedContentCard.vue';
import {
	canViewDiscussions,
	canViewFileList,
	useCanSeeCategoryWidget,
	useCanSeeMemberWidget,
} from '@front-app/util/permission';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import MemberList from '@front-app/components/group/MemberList.vue';
import { useDiscussionFollow } from '@front-app/components/discussion/useDiscussionFollow';
import { SORT_BY_ALPH_ASC_ICON, SORT_BY_DATE_ICON, FontAwesomeIconProps } from '@front-app/vendor/icon-definitions';
import { getGlobalContext } from '@front-app/util/env';
import { useContentRouting } from '@front-app/modules/content/hooks/useContentRouting';
import { GroupAuditAction } from '@front-app/services/ActionApi/ActionModel';
import { trackGroupContentAction, trackGroupSimpleAction } from '@front-app/services/ActionApi/ActionApi';
import { getGroupMemberPage } from '@front-app/services/GroupsApi/GroupsMemberApi';
import { ACTIVE_MEMBER_ROLES, GroupMember } from '@front-app/services/UserApi/UserModel';
import { MemberSort } from '@front-app/modules/groupMember/hooks/useSortMemberRadio';
import ConfirmationModal from '@front-app/components/modals/ConfirmationModal.vue';
import StandardErrorText from '@front-app/components/cards/state/StandardErrorText.vue';
import { useGroupContentStore } from '@front-app/store/piniaModules/groupContent';
import { syncedRef } from '@front-app/util/reactivity';

interface ContentPageQueryParams {
	search: string;
	categories: string[];
	types: ContentType[];
	sort: CombinedSortOrder;
}

const CONTENT_TYPE_OPTIONS = [
	ContentType.DISCUSSION,
	ContentType.LINK,
	ContentType.FILE,
];

const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
	[ContentType.DISCUSSION]: 'content.type.discussion',
	[ContentType.LINK]: 'content.type.link',
	[ContentType.FILE]: 'content.type.file',
};

enum CombinedSortOrder {
	ALPHABETICAL_ASC = 'alphabetical-asc',
	DATE_DESC = 'date-desc',
}

const SORT_CHOICES = [
	CombinedSortOrder.DATE_DESC,
	CombinedSortOrder.ALPHABETICAL_ASC,
];

const SORT_TYPE_LABELS: Record<CombinedSortOrder, string> = {
	[CombinedSortOrder.ALPHABETICAL_ASC]: 'content.sort.alphabetical',
	[CombinedSortOrder.DATE_DESC]: 'content.sort.most-recent',
};

const SORT_TO_ICON: Record<CombinedSortOrder, FontAwesomeIconProps> = {
	[CombinedSortOrder.ALPHABETICAL_ASC]: SORT_BY_ALPH_ASC_ICON,
	[CombinedSortOrder.DATE_DESC]: SORT_BY_DATE_ICON,
};

const SORT_TO_SORT: Record<CombinedSortOrder, ContentSort> = {
	[CombinedSortOrder.ALPHABETICAL_ASC]: ContentSort.NAME,
	[CombinedSortOrder.DATE_DESC]: ContentSort.DATE,
};

const SORT_TO_ORDER: Record<CombinedSortOrder, ContentOrdering> = {
	[CombinedSortOrder.ALPHABETICAL_ASC]: ContentOrdering.ASCENDING,
	[CombinedSortOrder.DATE_DESC]: ContentOrdering.DESCENDING,
};

const CONTENT_INFO_PANEL_BY_TYPE = {
	[ContentType.FILE]: ContentInfoModal,
	[ContentType.LINK]: ContentInfoModal,
};

const CONTENT_INFO_PANEL_ARIA_LABEL_BY_TYPE = {
	[ContentType.FILE]: 'discussion.aria.open-file-information-panel',
	[ContentType.LINK]: 'discussion.aria.open-link-information-panel',
	[ContentType.DISCUSSION]: 'discussion.aria.open-discussion',
};

const cardIdForContent = (content: Content | ContentV2): string => `contentCard_${content.id}_${content.type}`;

// How many to show per page in the browser
const LOCAL_PAGE_SIZE = 10;
// How many to prefetch
const PREFETCH_PAGE_SIZE = 20;
// The offset at which point we will prefetch another batch
const PREFETCH_OFFSET = LOCAL_PAGE_SIZE;

export default defineComponent({
	components: {
		PaddedContent,
		LoaderState,
		CtaButton,
		ScrollingColumn,
		SearchBox,
		CheckboxList,
		RadioList,
		FontAwesomeIcon,
		SlidingPanel,
		CategoriesList,
		MemberList,
		MixedContentCard,
		ConfirmationModal,
		StandardErrorText,
	},
	props: {
		groupId: {
			type: String,
			required: true,
		},
		search: {
			type: String,
			default: '',
		},
		categories: {
			type: Array as PropType<string[]>,
			default: () => [],
		},
		types: {
			type: Array as PropType<ContentType[]>,
			default: () => CONTENT_TYPE_OPTIONS,
		},
		sort: {
			type: String as PropType<CombinedSortOrder>,
			default: CombinedSortOrder.DATE_DESC,
		},
		showContent: {
			type: String,
			default: '',
		},
	},
	setup(props, ctx) {
		const group = computed(() => GroupsStore.getInstance().groupById(props.groupId));
		const trackActionProps = computed(() => ({
			groupId: props.groupId,
			search: props.search,
			categories: props.categories,
			types: props.types,
			sort: props.sort,
		}));

		const trackContentPageView = () => {
			trackGroupSimpleAction(props.groupId, GroupAuditAction.VIEW_CONTENT_PAGE);
		};
		watch(trackActionProps, (curr, prev) => {
			if (deepequal(curr, prev) || !props.groupId) {
				return;
			}
			trackContentPageView();
		}, { immediate: true });

		// Filters
		const searchValueLocal = ref(props.search);

		const categoryCheckboxes = useCheckboxes(ctx, {
			choiceKeys: computed(() => (group.value?.categories || [])),
			initialChoices: computed(() => props.categories),
			labelFn: (_) => _,
		});
		const categoryChoices = categoryCheckboxes.choices;
		const selectedCategoriesLocal = categoryCheckboxes.selectedChoices;
		const toggleLocalCategory = categoryCheckboxes.toggle;

		const filterTypeOptions = (option: ContentType) => {
			switch (option) {
			case ContentType.FILE:
			case ContentType.LINK:
				return canViewFileList(group.value);
			case ContentType.DISCUSSION:
				return canViewDiscussions(group.value);
			default:
				return false;
			}
		};
		const availableTypeOptions = computed(() => CONTENT_TYPE_OPTIONS.filter(filterTypeOptions));

		const typeCheckboxes = useCheckboxes(ctx, {
			choiceKeys: availableTypeOptions,
			initialChoices: computed(() => (props.types.length > 0 ? props.types : availableTypeOptions.value)),
			labels: CONTENT_TYPE_LABELS,
		});
		const typeChoices = typeCheckboxes.choices;
		const selectedTypesLocal = typeCheckboxes.selectedChoices;
		const toggleLocalType = typeCheckboxes.toggle;

		const selectedTypeLabels = computed(() => selectedTypesLocal.value
			.map((t) => ctx.root.$t(CONTENT_TYPE_LABELS[t]))
			.join(', '));

		const {
			choices: sortChoices,
			selectedChoice: selectedSortLocal,
			select: selectSortLocal,
		} = useRadioButtons(ctx, {
			choiceKeys: ref(SORT_CHOICES),
			labels: SORT_TYPE_LABELS,
			initialChoice: props.sort,
		});
		// END Filters

		// Group content store
		const params: Ref<ContentPageParamsV2> = ref({
			search: toWildcardSearch(props.search),
			category: props.categories,
			type: selectedTypesLocal.value,
			order: SORT_TO_ORDER[props.sort],
			sort: SORT_TO_SORT[props.sort],
			pageSize: PREFETCH_PAGE_SIZE,
		});
		const {
			cancelSearch,
			fetchNextPage,
			resetToFirstPage,
			fetchAttachedTo,
			update,
			contents,
			hasMorePages,
			isFetchingAttachedTo,
			status: contentFetchStatus,
		} = getGroupContentStore(computed(() => props.groupId), params);
		const pagingWindow = ref(LOCAL_PAGE_SIZE);
		const loadMore = () => {
			pagingWindow.value += LOCAL_PAGE_SIZE;
			if (
				hasMorePages
				&& (contents.value.length - PREFETCH_OFFSET) <= pagingWindow.value
				&& contentFetchStatus.value !== STATUS.LOADING
			) {
				fetchNextPage();
				trackContentPageView();
			}
		};
		const filteredContents = computed(() => contents.value);
		const visibleContents = syncedRef(() => filteredContents.value.slice(0, pagingWindow.value)).ref;
		const hasMoreLocalPages = computed(() => filteredContents.value.length >= pagingWindow.value);
		// END Group content store

		// Update Search
		const allQueryProps: Ref<ContentPageQueryParams> = computed(() => ({
			search: props.search,
			categories: props.categories,
			types: props.types,
			sort: props.sort,
		}));
		const updateSearchWithParams = async () => {
			pagingWindow.value = LOCAL_PAGE_SIZE;
			params.value = {
				search: toWildcardSearch(allQueryProps.value.search),
				category: allQueryProps.value.categories,
				type: selectedTypesLocal.value,
				sort: SORT_TO_SORT[allQueryProps.value.sort],
				order: SORT_TO_ORDER[allQueryProps.value.sort],
				pageSize: PREFETCH_PAGE_SIZE,
			};
			await resetToFirstPage();
		};
		watch(allQueryProps, (a: ContentPageQueryParams, b: ContentPageQueryParams | undefined) => {
			if (deepequal(a, b)) {
				return;
			}
			updateSearchWithParams();
		}, { immediate: true });
		useEventBus().on(EventBusEvent.DISCUSSION_CREATED, () => {
			if (selectedTypesLocal.value.includes(ContentType.DISCUSSION)) {
				updateSearchWithParams();
			}
		});
		useEventBus().on(EventBusEvent.FILES_UPLOADED, () => {
			if (selectedTypesLocal.value.includes(ContentType.FILE)) {
				updateSearchWithParams();
			}
		});
		useEventBus().on(EventBusEvent.LINK_SHARED, () => {
			if (selectedTypesLocal.value.includes(ContentType.LINK)) {
				updateSearchWithParams();
			}
		});
		const updateSearch = async () => {
			const query: ContentPageQueryParams = {
				search: searchValueLocal.value,
				categories: selectedCategoriesLocal.value,
				types: selectedTypesLocal.value,
				sort: selectedSortLocal.value,
			};
			ctx.root.$router.push({
				query: { ...query },
				name: Routes.GROUP_CONTENT,
				params: {
					groupId: props.groupId,
				},
			});
		};

		const selectSort = async (val: CombinedSortOrder) => {
			selectSortLocal(val);
			updateSearch();
		};

		let searchTid: number;
		const debouncedSearchUpdate = () => {
			cancelSearch();
			clearTimeout(searchTid);
			searchTid = getGlobalContext().setTimeout(() => {
				updateSearch();
			}, 100);
		};
		watch(searchValueLocal, (newVal, oldVal) => {
			if (newVal === oldVal) {
				return;
			}
			if (newVal === '') {
				updateSearch();
			} else {
				debouncedSearchUpdate();
			}
		});
		// END Update search

		// Content info panel
		const groupContentStore = useGroupContentStore();
		const selectedContent = syncedRef(() => groupContentStore.getById(groupContentStore.selectedContentId)).ref;
		const contentListEl: Ref<null | HTMLElement> = ref(null);
		const displayContent = (content: ContentV2) => {
			if (content.type === ContentType.DISCUSSION) {
				ctx.root.$router.push({
					name: Routes.GROUP_DISCUSSION,
					params: {
						discussionId: content.id,
						groupId: props.groupId,
					},
				});
			} else {
				fetchAttachedTo(content.id);
				groupContentStore.selectContent(content.id);
				const action = content.type === ContentType.FILE ? GroupAuditAction.VIEW_FILE : GroupAuditAction.VIEW_LINK;
				trackGroupContentAction(props.groupId, content.id, action);
			}
		};
		const {
			displayInNewTab: displayContentInNewTab,
		} = useContentRouting(ctx.root.$router);
		const closeContentModal = () => {
			if (!selectedContent.value) {
				return;
			}

			// Return focus to opener
			const child = contentListEl.value?.querySelector(
				`#${cardIdForContent(selectedContent.value)} .content-card--clickable_aria-button-internal`,
			) as HTMLButtonElement | undefined;
			if (child) {
				child.focus();
			}

			groupContentStore.selectContent('');
			const q = ctx.root.$route.query;

			ctx.root.$router.push({ query: { ...q, showContent: '' } });
		};
		const routeToContent = (content: ContentV2) => {
			if (selectedContent.value?.id !== content.id) {
				displayContent(content);
			}
			if (content.type !== ContentType.DISCUSSION) {
				const q = ctx.root.$route.query;
				ctx.root.$router.push({ query: { ...q, showContent: content.id } });
			}
		};
		watch(() => props.showContent, async (curr, prev) => {
			if (!curr || curr === prev || selectedContent.value?.id === curr) {
				return;
			}
			const content = await getGroupContentV2(props.groupId, props.showContent);
			groupContentStore.update([content]);
			displayContent(content);
		}, { immediate: true });
		// END Content info panel

		// Discussion (un)follow
		const toggleFollow = (content: ContentV2) => {
			if (content.type !== ContentType.DISCUSSION) {
				return;
			}
			const { toggleLocalFollowState } = useDiscussionFollow(ref(content));
			toggleLocalFollowState();
		};
		// TODO: refactor to use Pinia
		const toggleFollowById = (requestedId: string) => {
			const item = contents.value.find(({ id }) => id === requestedId);
			if (item) {
				toggleFollow(item);
			}
		};
		// END Discussion (un)follow

		// Members
		const canSeeMembers = useCanSeeMemberWidget(group);
		const {
			track,
			isLoading: isLoadingMembers,
		} = useAsyncStatus();
		const activeMembers: Ref<GroupMember[]> = ref([]);
		const memberFetcher = async () => {
			activeMembers.value = await track(getGroupMemberPage(props.groupId, ACTIVE_MEMBER_ROLES, 1, MemberSort.LAST_JOIN));
		};
		watch(() => props.groupId, (curr, prev) => {
			if (canSeeMembers.value && curr && curr !== prev) {
				memberFetcher();
			}
		}, { immediate: true });
		// END Members

		// Categories
		const canSeeCategories = useCanSeeCategoryWidget(group);
		// END Categories

		// Delete
		const deleteContentModal = useConfirmDialog();
		const contentSelectedForDeletion: Ref<ContentV2 | null> = ref(null);
		const showDeleteContentModal = async (content: ContentV2) => {
			if (content.type === ContentType.DISCUSSION) {
				return;
			}
			contentSelectedForDeletion.value = content;
			deleteContentModal.reveal();
		};
		const {
			track: trackContentDelete,
			isLoading: isDeletingContent,
			isError: errorDeletingContent,
		} = useAsyncStatus();
		const performContentDeletion = async () => {
			const content = contentSelectedForDeletion.value;
			if (!content) {
				return trackContentDelete(Promise.reject());
			}
			if (content.type === ContentType.LINK) {
				await trackContentDelete(deleteLink(content.groupId, content.id));
			} else if (content.type === ContentType.FILE) {
				await trackContentDelete(deleteFile(content.groupId, content.id));
			}
			deleteContentModal.confirm();
			contentSelectedForDeletion.value = null;
			await resetToFirstPage();
			return Promise.resolve();
		};
		// END delete

		// Reactions
		const reactToContent = async (contentType: ContentType, contentId: string, reactionType: ReactionType, parentDiscussionId = '') => {
			await groupContentStore.react(props.groupId, contentType, contentId, reactionType);

			// Update the content or the whole discussion in the old content store
			let updatedContentInNewStore;
			if (parentDiscussionId) {
				updatedContentInNewStore = groupContentStore.getById(parentDiscussionId);
			} else {
				updatedContentInNewStore = groupContentStore.getById(contentId);
			}
			if (updatedContentInNewStore) {
				update([updatedContentInNewStore]);
			}
		};
		// END Reactions

		return {
			// Constants
			Spacing,
			Size,
			ButtonPalettes,
			STATUS,
			// Content paging
			visibleContents,
			contentFetchStatus,
			loadMore,
			hasMorePages: hasMoreLocalPages,
			// Filters
			group,
			updateSearch,
			searchValueLocal,
			// Filters / Categories
			categoryChoices,
			selectedCategoriesLocal,
			toggleLocalCategory,
			// Filters / Types
			typeChoices,
			selectedTypesLocal,
			selectedTypeLabels,
			toggleLocalType,
			// Filters / Sort
			sortChoices,
			selectedSortLocal,
			selectSort,
			SORT_TO_ICON,
			// Content Modal
			selectedContent,
			cardIdForContent,
			contentListEl,
			isFetchingAttachedTo,
			routeToContent,
			displayContentInNewTab,
			closeContentModal,
			CONTENT_INFO_PANEL_BY_TYPE,
			CONTENT_INFO_PANEL_ARIA_LABEL_BY_TYPE,
			// Discussions
			toggleFollow,
			toggleFollowById,
			// Members list
			canSeeMembers,
			activeMembers,
			isLoadingMembers,
			// Categories list
			canSeeCategories,
			// Delete content
			deleteContentModal,
			contentSelectedForDeletion,
			showDeleteContentModal,
			isDeletingContent,
			errorDeletingContent,
			performContentDeletion,
			// Reactions
			reactToContent,
			// Util
			elide(val: string, len = 2): string {
				if (val.length <= len) {
					return val;
				}
				return `${val.substring(0, len)}…`;
			},
		};
	},
});
